import { html, TemplateResult } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { RmScoreBreakdown, RmTopResourcesAtRiskPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { WEB_COMPONENT_GZ_RM_TOP_RESOURCES_RISK_WIDGET } from '../../../../webcomponents.constants';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { renderNumber, renderText } from '../../text';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { FontSizes } from '../../../../types/common/types';
import { translateFn } from '../../../../utils/translator.helper';
import { ColumnKeys as DevicesAppColumnKeys } from '../../../devicesApp/types';
import { buildOverlayContent, closeOverlay } from '../../../common/customRenderers';
import { ContextMenuOverlayElement } from '../../../../ui/gzContextMenuOverlay';
import { RiskTypes, State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget } from '../../resize';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-top-resources-risk-widget
 */
@customElement(WEB_COMPONENT_GZ_RM_TOP_RESOURCES_RISK_WIDGET)
export class GzRmTopResourcesRiskWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmTopResourcesAtRiskPayload = { topResourcesAtRisk: { companyId: '', total: 0, items: [] } };

  /**
   * WARNING: this query MUST be cached, because after the first render of the overlay, it will
   * be removed from the shadow DOM and replaced by a placeholder while open.
   */
  @query('#risk-overlay', true)
  overlay!: ContextMenuOverlayElement;

  static get styles() {
    return styles;
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.topResourcesAtRisk?.companyId ?? '' },
      filters: [
        {
          [DevicesAppColumnKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [DevicesAppColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      section: 'resources',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    const items: Row[] =
      this.payload.topResourcesAtRisk?.items?.map(
        ({ id, type, name, scoreBreakdown }): RowConfig => ({
          data: { id, name, type },
          cells: [
            html` <gz-rm-icon iconSet="entity" iconClassMapType="devices" type="${type}"></gz-rm-icon>`,
            renderText(name),
            html` <div
              class="d-contents"
              @mouseenter="${e => this.showOverlay(e, scoreBreakdown)}"
              @mouseleave="${e => this.closeOverlay()}"
            >
              <gz-rm-severity-bar score="${scoreBreakdown.finalScore}"></gz-rm-severity-bar>
            </div>`,
            renderNumber(`${scoreBreakdown.finalScore}%`),
          ],
        })
      ) ?? [];

    const grayBullet = html` <gz-bullet
      boxSize="${FontSizes.BODY}"
      size="${FontSizes.HEADING_5}"
      center
      color="var(--grey-layout-202-disabled-icons)"
    ></gz-bullet>`;
    const emptyBar = html` <gz-rm-severity-bar score="0"></gz-rm-severity-bar>`;
    const emptyItem = [grayBullet, renderText('...', true), emptyBar, renderNumber('--', true)];
    const fullItems = fitItemsToWidget(fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem), this.contentHeight);

    return html`
      <gz-basic-table
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { id: string; name: string; type: number }) => {
          if (!rowData) {
            return;
          }

          const { id, name, type } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.topResourcesAtRisk?.companyId ?? '' },
            section: 'resources',
            filters: [
              {
                [DevicesAppColumnKeys.DEVICE_NAME]: {
                  complexEnumFilter: [
                    {
                      label: name,
                      value: id,
                      type,
                    },
                  ],
                },
              },
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      <gz-context-menu-overlay id="risk-overlay" class="fit-content"></gz-context-menu-overlay>

      <gz-link type="${LinkType.QUIET}" fontSize="${FontSizes.PARAGRAPH_11}" .callback=${() => this.viewAll()}>
        ${translateFn('View all')} (${this.payload.topResourcesAtRisk?.total ?? 0})
      </gz-link>
    `;
  }

  private async showOverlay(event: unknown, scoreBreakdown: RmScoreBreakdown): Promise<void> {
    await buildOverlayContent(event, this.overlay, { scoreBreakdown }, RiskTypes.DEVICES);
  }

  private async closeOverlay(): Promise<void> {
    await closeOverlay(this.overlay);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-top-resources-risk-widget': GzRmTopResourcesRiskWidgetWebComponent;
  }
}
